/**
 * Anmeldung: Supabase Auth mit E-Mail + Passwort und – wenn ein Admin den Schalter
 * „Zweiter Faktor bei der Anmeldung“ eingeschaltet hat – zweitem Faktor (TOTP aus
 * einer Authenticator-App). Ob der zweite Faktor verlangt wird, sagt der Server
 * (401 mit `code: 'second_factor_required'`). Passwörter, Codes und Ausweise (JWT)
 * prüft und stellt nur Supabase aus; hier wird nichts davon gespeichert oder geloggt.
 *
 * Nur im CI-Stack (Server meldet testLogin) kommt der Ausweis stattdessen aus
 * dem sessionStorage – dort legt ihn der automatische Test ab.
 */
import { createClient, isAuthError, type SupabaseClient } from '@supabase/supabase-js';

export interface PublicConfig {
  supabaseUrl: string | null;
  supabasePublishableKey: string | null;
  testLogin: boolean;
}

export const TEST_TOKEN_KEY = 'till-erp.test-token';

/** Wo die Anmeldung gerade steht. */
export type LoginStep =
  | { kind: 'password' }
  /** Passwort stimmt, aber noch kein zweiter Faktor: Authenticator-App einrichten. */
  | { kind: 'enroll'; factorId: string; qrCode: string; secret: string }
  /** Passwort stimmt, Faktor vorhanden: Code aus der App eingeben. */
  | { kind: 'code'; factorId: string }
  /** Angemeldet: bei currentStep mit Passwort (aal1 oder aal2), bei secondFactorStep mit zweitem Faktor (aal2). */
  | { kind: 'done' };

/** Fehler der Anmeldung mit deutschem Satz (Ursache + nächster Schritt). */
export class LoginError extends Error {
  constructor(
    message: string,
    /** Welches Feld betroffen ist, damit die Seite den Fehler dort zeigt. */
    readonly field: 'password' | 'code' | null = null,
  ) {
    super(message);
    this.name = 'LoginError';
  }
}

export const LOGIN_TEXTS = {
  wrongPassword: 'E-Mail oder Passwort stimmen nicht.',
  wrongCode: 'Der Code stimmt nicht. Bitte den aktuellen Code aus der App eingeben.',
  codeFormat: 'Der Code hat 6 Ziffern. Bitte den aktuellen Code aus der App eingeben.',
  tooMany: 'Zu viele Versuche. Bitte warte ein paar Minuten und versuche es dann noch einmal.',
  general: 'Die Anmeldung hat nicht geklappt. Bitte versuche es noch einmal; wenn es wieder passiert, Rudolf ansprechen.',
  offline: 'Der Anmeldedienst ist gerade nicht erreichbar. Prüfe die Internetverbindung und versuche es gleich noch einmal.',
  testMode: 'Im Prüfmodus gibt es keine Anmeldung mit Passwort.',
  /** Gesperrt (D4): gleicher Satz wie die Antwort des ERP-Servers. */
  blocked: 'Dein Zugang ist gesperrt. Bitte Rudolf ansprechen.',
  linkUsed: 'Dieser Link ist schon benutzt oder abgelaufen. Bitte Rudolf ansprechen.',
  linkBroken: 'Dieser Link ist unvollständig. Bitte den ganzen Link aus der Nachricht öffnen oder Rudolf ansprechen.',
  passwordMissing: 'Bitte ein Passwort eingeben.',
  passwordMismatch: 'Die beiden Passwörter sind nicht gleich. Bitte noch einmal eingeben.',
  passwordWeak: 'Das Passwort ist zu kurz oder zu einfach. Bitte ein längeres Passwort wählen, am besten aus dem Passwort-Manager.',
  emailMissing: 'Bitte deine E-Mail-Adresse eingeben.',
  resetSent:
    'Wenn es zu dieser Adresse einen Zugang gibt, kommt gleich eine E-Mail mit einem Link. Damit setzt du ein neues Passwort. Keine E-Mail? Bitte Rudolf ansprechen.',
} as const;

export interface AuthClient {
  /** Ausweis für die ERP-Schnittstelle; ob aal1 reicht, entscheidet der Server per Einstellung. */
  getAccessToken(): Promise<string | null>;
  /** Ohne Sitzung `password`, sonst `done` (mit Passwort angemeldet). */
  currentStep(): Promise<LoginStep>;
  /**
   * Nur wenn der Server den zweiten Faktor verlangt: `done` bei Stufe aal2, sonst Code
   * eingeben oder – ohne Faktor – Authenticator-App einrichten (legt den Faktor an).
   */
  secondFactorStep(): Promise<LoginStep>;
  signInWithPassword(email: string, password: string): Promise<void>;
  /** Prüft den 6-stelligen Code (Einrichten und normale Anmeldung). */
  verifyCode(factorId: string, code: string): Promise<void>;
  /** „Passwort vergessen“: Supabase schickt einen Link an die Adresse (ohne zu verraten, ob es sie gibt). */
  requestPasswordReset(email: string): Promise<void>;
  /** Einrichtungs-Link einlösen (`token_hash` aus dem Fragment); danach mit Passwort angemeldet (aal1). */
  redeemSetupLink(tokenHash: string): Promise<void>;
  /** Bestätigter TOTP-Faktor der angemeldeten Person (dann verlangt Supabase für ein neues Passwort aal2). */
  verifiedFactorId(): Promise<string | null>;
  /** Neues Passwort für die angemeldete Person (nach dem Einlösen des Links). */
  setPassword(password: string): Promise<void>;
  signOut(): Promise<void>;
  /** Meldet nur Abmeldungen von außen (abgelaufen, anderer Tab), nicht jeden Zwischenschritt. */
  onSignedOut(listener: () => void): () => void;
}

export async function loadPublicConfig(): Promise<PublicConfig> {
  const res = await fetch('/api/v1/config');
  if (!res.ok) throw new Error('Konfiguration nicht erreichbar');
  return (await res.json()) as PublicConfig;
}

/** Code ohne Leerzeichen („123 456“ → „123456“); gemeinsame Bereinigung für Feld und Prüfung. */
export function stripCodeSpaces(input: string): string {
  return input.replace(/\s+/g, '');
}

/** Nur Ziffern, Leerzeichen dürfen mit eingegeben werden („123 456“). */
export function normalizeCode(input: string): string | null {
  const code = stripCodeSpaces(input);
  return /^\d{6}$/.test(code) ? code : null;
}

function toLoginError(error: unknown, field: 'password' | 'code'): LoginError {
  if (isAuthError(error)) {
    const code = error.code ?? '';
    if (code === 'invalid_credentials') return new LoginError(LOGIN_TEXTS.wrongPassword, 'password');
    if (code === 'user_banned') return new LoginError(LOGIN_TEXTS.blocked, null);
    if (code === 'otp_expired' || code === 'flow_state_expired' || code === 'flow_state_not_found') return new LoginError(LOGIN_TEXTS.linkUsed, null);
    if (code === 'weak_password' || code === 'same_password') return new LoginError(LOGIN_TEXTS.passwordWeak, 'password');
    if (code === 'mfa_verification_failed' || code === 'mfa_challenge_expired') {
      return new LoginError(LOGIN_TEXTS.wrongCode, 'code');
    }
    if (code === 'over_request_rate_limit' || code === 'over_email_send_rate_limit' || error.status === 429) {
      return new LoginError(LOGIN_TEXTS.tooMany, field);
    }
    if (error.status === 0) return new LoginError(LOGIN_TEXTS.offline, null);
  }
  return new LoginError(LOGIN_TEXTS.general, null);
}

function createTestModeClient(): AuthClient {
  const listeners = new Set<() => void>();
  return {
    async getAccessToken() {
      return window.sessionStorage.getItem(TEST_TOKEN_KEY);
    },
    async currentStep() {
      return window.sessionStorage.getItem(TEST_TOKEN_KEY) ? { kind: 'done' } : { kind: 'password' };
    },
    async secondFactorStep() {
      // Prüf-Ausweise haben ihre Stufe fest eingebaut; einen Code-Schritt gibt es hier nicht.
      return { kind: 'done' };
    },
    async signInWithPassword() {
      throw new LoginError(LOGIN_TEXTS.testMode);
    },
    async verifyCode() {
      throw new LoginError(LOGIN_TEXTS.testMode);
    },
    async requestPasswordReset() {
      throw new LoginError(LOGIN_TEXTS.testMode);
    },
    async redeemSetupLink() {
      throw new LoginError(LOGIN_TEXTS.testMode);
    },
    async verifiedFactorId() {
      return null;
    },
    async setPassword() {
      throw new LoginError(LOGIN_TEXTS.testMode);
    },
    async signOut() {
      window.sessionStorage.removeItem(TEST_TOKEN_KEY);
      listeners.forEach((l) => l());
    },
    onSignedOut(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export function createAuthClient(config: PublicConfig): AuthClient {
  if (config.testLogin) return createTestModeClient();
  if (!config.supabaseUrl || !config.supabasePublishableKey) {
    throw new Error('Supabase ist auf dem Server nicht eingerichtet (SUPABASE_URL / SUPABASE_PUBLISHABLE_KEY).');
  }
  const supabase: SupabaseClient = createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false },
  });
  // Ein angefangenes Einrichten nicht doppelt anlegen (z. B. zwei gleichzeitige Abfragen).
  let pendingEnroll: Promise<LoginStep> | null = null;

  async function enroll(): Promise<LoginStep> {
    const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
    if (listError) throw listError;
    // Nicht abgeschlossene Einrichtungen (Seite neu geladen) aufräumen, dann neu anlegen.
    for (const f of factors.all) {
      if (f.factor_type === 'totp' && f.status === 'unverified') {
        const { error } = await supabase.auth.mfa.unenroll({ factorId: f.id });
        if (error) throw error;
      }
    }
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'TILL ERP', issuer: 'TILL ERP' });
    if (error) throw error;
    return { kind: 'enroll', factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
  }

  return {
    async getAccessToken() {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token ?? null;
    },
    async currentStep() {
      const { data: session } = await supabase.auth.getSession();
      return session.session ? { kind: 'done' } : { kind: 'password' };
    },
    async secondFactorStep() {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) return { kind: 'password' };
      const { data: level, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) throw toLoginError(error, 'password');
      if (level.currentLevel === 'aal2') return { kind: 'done' };
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw toLoginError(listError, 'password');
      const verified = factors.totp[0];
      if (verified) return { kind: 'code', factorId: verified.id };
      pendingEnroll ??= enroll().finally(() => {
        pendingEnroll = null;
      });
      try {
        return await pendingEnroll;
      } catch (err) {
        throw toLoginError(err, 'code');
      }
    },
    async signInWithPassword(email, password) {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) throw toLoginError(error, 'password');
    },
    async verifyCode(factorId, input) {
      const code = normalizeCode(input);
      if (!code) throw new LoginError(LOGIN_TEXTS.codeFormat, 'code');
      const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
      if (error) throw toLoginError(error, 'code');
    },
    async requestPasswordReset(email) {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}/einrichten` });
      // Kein Hinweis, ob es die Adresse gibt; nur Netz- und Mengenfehler melden.
      if (error && (error.status === 0 || error.status === 429 || error.code === 'over_email_send_rate_limit')) throw toLoginError(error, 'password');
    },
    async redeemSetupLink(tokenHash) {
      const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' });
      if (error) {
        const mapped = toLoginError(error, 'password');
        // Ungültig oder schon benutzt: GoTrue meldet 403/otp_expired; andere 4xx ebenso behandeln.
        if (mapped.message === LOGIN_TEXTS.general && (error.status ?? 0) >= 400 && (error.status ?? 0) < 500) throw new LoginError(LOGIN_TEXTS.linkUsed);
        throw mapped;
      }
    },
    async verifiedFactorId() {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw toLoginError(error, 'code');
      return data.totp[0]?.id ?? null;
    },
    async setPassword(password) {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw toLoginError(error, 'password');
    },
    async signOut() {
      // Nur dieses Gerät abmelden, nicht alle Sitzungen des Kontos.
      await supabase.auth.signOut({ scope: 'local' });
    },
    onSignedOut(listener) {
      const { data } = supabase.auth.onAuthStateChange((event) => {
        if (event === 'SIGNED_OUT') listener();
      });
      return () => data.subscription.unsubscribe();
    },
  };
}
